import { SortingRule } from 'react-table';

import { LOCALE } from '../../../../utils/i18n';
import { AsyncResult, Action, ApiAction } from '../../../index';
import api from '../../../redux-api';

import { GET_ENTERPRISE_PORTFOLIOS, DELETE_ENTERPRISE_GROUP_PORTFOLIOS } from './shared';
import EnterpriseAdminDetails from '@/types/enterprise-admin-details';
import Locale from '@/types/locale';
import PaginatedPortfolios from '@/types/paginated-portfolios';
import Portfolio from '@/types/portfolio';

const SAVE_ENTERPRISE_GROUP_PORTFOLIO: ApiAction = {
  type: 'connect/enterprises/add-group-portfolio',
  endpoint: 'enterprises/:id/groups/:groupId/portfolios',
};

const GET_ENTERPRISE_GROUP_PORTFOLIOS_BY_ID: ApiAction = {
  type: 'connect/enterprise/group/portfolios-by-id',
  endpoint: 'enterprises/:id/groups/:groupId/portfolios',
};

// Reducer
export default function reducer(state: EnterpriseAdminDetails, action: Action): EnterpriseAdminDetails {
  if (state === null) {
    return null;
  }

  switch (action.type) {
    case `${GET_ENTERPRISE_GROUP_PORTFOLIOS_BY_ID.type}_SUCCESS`: {
      let { portfolios } = action.data;
      return {
        ...state,
        groups: state.groups.map(group => {
          if (group._id !== action.data.groupId) {
            return group;
          }

          if (action.data.next) {
            const current = group.portfolios || [];
            portfolios = current.concat(portfolios);
          }

          return {
            ...group,
            portfolios,
            nextPortfoliosToken: action.data.next,
          };
        }),
      };
    }
    default: {
      return state;
    }
  }
}

export const getEnterprisePortfolios = (groupId?: string, query?: string, nextToken?: string, sorting?: SortingRule[]): AsyncResult<PaginatedPortfolios> => {
  type GetParams = {
    id: string;
    locale: Locale;
    query?: string;
    next?: string;
    groupId?: string;
    sort?: string;
  };

  return (dispatch, getState) => {
    const state = getState();
    const {
      enterprise: { details },
    } = state;
    let {
      intl: { locale },
    } = state;

    if (!details) {
      return Promise.reject();
    }

    // TODO - Support en-US filters [CON-8332]
    if (locale === LOCALE.EN_US) {
      locale = LOCALE.EN_CA;
    }

    const params: GetParams = { id: details._id, locale };

    if (query) {
      params.query = query;
    }

    if (nextToken) {
      params.next = nextToken;
    }

    if (groupId) {
      params.groupId = groupId;
    }

    if (sorting && sorting.length > 0) {
      params.sort = `${sorting[0].id} ${sorting[0].desc ? 'desc' : 'asc'}`;
    }

    const action = groupId ? GET_ENTERPRISE_GROUP_PORTFOLIOS_BY_ID : GET_ENTERPRISE_PORTFOLIOS;

    // TODO modify group portfolios endpoint to match data.portfolios, or vice versa
    return api.get(action, params).then((res: { data: any }) => res.data);
  };
};

export const saveEnterpriseGroupPortfolio =
  (enterpriseId: string, groupId: string, portfolio: Portfolio): AsyncResult<void> =>
  () =>
    api.post(SAVE_ENTERPRISE_GROUP_PORTFOLIO, { id: enterpriseId, groupId, portfolioId: portfolio._id });

export const deleteEnterpriseGroupPortfolios =
  (groupId: string, portfolios: Portfolio[]): AsyncResult<void> =>
  (dispatch, getState) => {
    const {
      enterprise: { details },
    } = getState();

    if (!details) {
      return Promise.reject();
    }

    const params = {
      id: details._id,
      groupId,
      portfolioIds: portfolios.map(portfolio => portfolio._id).join(';'),
    };

    return api.delete(DELETE_ENTERPRISE_GROUP_PORTFOLIOS, params);
  };
